import {
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { lockLoad, refreshLock, unlockLoad } from '../utils/api/load';
import { showErrorMessage, logger } from '../utils/common';
import { LOCK_EXPIRATION_STATES } from '../constants';

const TIME_DURATIONS = {
  expiringSoon: 2 * 60000, // 2 minutes
  requestLockTimeout: 3000, // 3 seconds (in MS)
};

/**
 * Custom effect for handling locking/unlocking of loads.
 * @param {object} loadInfo - Loadinfo object containing loadLock data
 * @param {func} openLockExpirationModal - Function to open the lock expiration modal wherever this hook is used.
 * @return {effect}
 */
const useLockLoad = (loadInfo, refetchData, openLockExpirationModal, noDelay) => {
  const lockTimeout = useRef(null);
  const [lock, setLock] = useState(null);
  const user = useSelector((store) => store.oidc.user);

  // =========== LOCK CALLBACKS ===========
  /**
   * Clears all the timeouts related to a given lock
   * @return {void}
   */
  const clearTimeouts = useCallback(() => {
    if (lockTimeout.current) {
      clearTimeout(lockTimeout.current);
      lockTimeout.current = null;
    }
  }, [lockTimeout]);

  /**
   * Extends a given lock. On success, triggers a callback, that
   * should go reach out for new lock data in the UI. Either loadSummary
   * or loadDetails usually is what is being refreshed in the callback.
   * @param {func} callback - Callback function to get updated lock data in UI
   * @return {void}
   */
  const extendLock = useCallback(() => {
    setLock({
      ...lock,
      loading: true,
    });
    refreshLock(lock.loadId).then((res) => {
      const { model } = res.data;
      setLock({
        ...lock,
        ...model,
        expiring: LOCK_EXPIRATION_STATES.expirationExtended,
      });
      refetchData();
    });
  }, [lock, refetchData]);

  /**
   * Helper function on lock that allow user to acknowledge a
   * locks expiration, and do nothing about it.
   */
  const acknowledgeExpiration = useCallback(() => {
    setLock({
      ...lock,
      expiring: LOCK_EXPIRATION_STATES.expirationAcknowledged,
    });
  }, [lock]);

  /**
     * Helper function that reaches out to the network to unlock a lock on a load
     * @return {void}
     */
  const clearLock = useCallback(() => {
    if (lock?.ownedByUser) {
      unlockLoad(lock.loadId).then(() => {
        logger.info(`LoadId: ${lock.loadId} is unlocked`);
      }).catch(() => {
        logger.warn(`No lock found for : ${lock.loadId}`);
      });
    }
  }, [lock]);

  // =========== BEGIN EFFECT ===========
  useEffect(() => {
    // =========== HELPER FUNCTIONS ===========
    /**
     * Helper functino to determine if a lock belongs to the user;
     * @param {object} lock - Lock Object
     * @param {*} user - User object
     * @return {boolean}
     */
    const lockBelongsToUser = (loadLock, userObject) => loadLock.initiatorName === userObject.username;

    /**
     * Determines the expiring state of a lock based on it's
     * end time and the systems current time.
     * @param {object} lock - Lock Object.
     * @return {number} - Expiring state
     */
    const getExpiringState = (loadLock) => {
      const lockExpiration = moment(loadLock.endTime);
      const timeRemaining = lockExpiration.diff(moment(), 'milliseconds');
      if (timeRemaining <= 0) {
        logger.log('lock expired. This should not happen');
      }
      if (timeRemaining <= TIME_DURATIONS.expiringSoon) {
        return LOCK_EXPIRATION_STATES.expiring;
      }

      return LOCK_EXPIRATION_STATES.fresh;
    };

    /**
     * Object that calculates the difference between system time and
     * the moment 2 minutes before a lock expires.
     * expiration time.
     * @param {object} lock - Lock Object
     * @return {number} - Difference in MS
     */
    const getDurationUntilTwoMinBeforeExpiration = (loadLock) => {
      const lockExpiration = moment(loadLock.endTime);
      const expiringTime = lockExpiration.subtract(TIME_DURATIONS.expiringSoon, 'milliseconds');
      return expiringTime.diff(moment(), 'milliseconds');
    };

    const getDurationUntilEndtime = (loadLock) => {
      const lockExpiration = moment(loadLock.endTime);
      return lockExpiration.diff(moment(), 'milliseconds');
    };

    /**
     * Marks a lock as expired
     * @param {object} loadLock - LoadLock object
     * @return {void}
     */
    const setLockExpired = (loadLock) => {
      clearTimeouts();
      setLock({
        ...loadLock,
        expiring: LOCK_EXPIRATION_STATES.expired,
      });
      logger.info(`LoadId: ${loadInfo.loadId} lock is expired`);
      openLockExpirationModal();
    };

    /**
     * Sets a given lock object to be 'expiring soon'
     * @param {object} loadLock - Lock object
     */
    const setLockExpiring = (loadLock) => {
      const newLock = {
        ...loadLock,
        expiring: LOCK_EXPIRATION_STATES.expiring,
      };
      setLock(newLock);
      logger.info(`LoadId: ${loadInfo.loadId} lock is expiring soon`);
      const timeTillExpiring = getDurationUntilEndtime(newLock);
      lockTimeout.current = setTimeout(() => setLockExpired(newLock), timeTillExpiring);
      openLockExpirationModal();
    };

    /**
     * Helper function that reaches out to the network for a new load lock
     * @return {void}
     */
    const requestLoadLock = () => {
      clearTimeouts();
      lockLoad(loadInfo.loadId).then((res) => {
        const { model } = res.data;
        const newLock = {
          ...model,
          expiring: LOCK_EXPIRATION_STATES.fresh,
          ownedByUser: true,
        };
        setLock(newLock);
        logger.info(`LoadId: ${loadInfo.loadId} is locked `);
        const timeTillExpiring = getDurationUntilTwoMinBeforeExpiration(model);
        lockTimeout.current = setTimeout(() => setLockExpiring(newLock), timeTillExpiring);
      }).catch(() => {
        clearTimeouts();
        logger.error(`Unable to set lock on load id: ${loadInfo.loadId}`);
        showErrorMessage('This load appears to be locked by another user. The application has now been refreshed.');
        refetchData();
      });
    };

    // =========== EFFECT HANDLER ===========
    // If a lock doesn't exist, and the loadSummary is populated, and we havent already set a timeout
    // to request a lock for this load
    if (!lock && loadInfo?.loadId && !lockTimeout.current) {
      // If a lock exists but it doesn't belong to the user, set it and don't set any timeouts.
      if (loadInfo.loadLock && !lockBelongsToUser(loadInfo.loadLock, user)) {
        clearTimeouts();
        setLock({
          ...loadInfo.loadLock,
          ownedByUser: false,
        });
      } else if (loadInfo.loadLock && lockBelongsToUser(loadInfo.loadLock, user)) {
        clearTimeouts();
        // If a lock exists and does belong to the user, set the lock and set appropriate timeouts
        const expiring = getExpiringState(loadInfo.loadLock);
        const newLock = {
          ...loadInfo.loadLock,
          ownedByUser: true,
          expiring,
        };
        setLock(newLock);
        // 2 minutes before the locks expiration time
        const timeTillExpiring = getDurationUntilTwoMinBeforeExpiration(loadInfo.loadLock);

        // If past 2 min before mark, set a timeout of 0
        // to trigger expiration modal immediatly.
        const timeoutLength = timeTillExpiring > 0 ? timeTillExpiring : 0;

        // Set 'lock expiring' modal to appear based on the above timeout duration.
        lockTimeout.current = setTimeout(() => setLockExpiring(newLock), timeoutLength);
        logger.info(`LoadId: ${loadInfo.loadLock.loadId} locked with existing lock`);
      } else {
        // If a lock does not exist, request a lock after a 3 second timeout
        let timeout = TIME_DURATIONS.requestLockTimeout;
        if (noDelay) {
          timeout = 0;
        }
        lockTimeout.current = setTimeout(requestLoadLock, timeout);
      }
    } else if (lock?.expiring === LOCK_EXPIRATION_STATES.expirationExtended) {
      clearTimeouts();
      const extendedLock = {
        ...lock,
        ownedByUser: true,
        expiring: LOCK_EXPIRATION_STATES.fresh,
      };
      setLock(extendedLock);
      const timeTillExpiring = getDurationUntilTwoMinBeforeExpiration(lock);
      const timeoutLength = timeTillExpiring > 0 ? timeTillExpiring : 0;
      lockTimeout.current = setTimeout(() => setLockExpiring(extendedLock), timeoutLength);
      logger.info(`LoadId: ${lock.loadId} lock was extended`);
    }

    // =========== EFFECT CLEANUP ===========
    return () => {
      if (!loadInfo) {
        clearTimeouts();
        setLock(null);

        // if the loadSummary is loading the a lock is set,
        // this signifies that we have changed loads
        if (lock?.loadId) {
          clearLock();
        }
      }
    };
  }, [loadInfo, lock, user, clearLock, clearTimeouts, openLockExpirationModal, noDelay, refetchData]);

  if (!lock) {
    return {
      clearTimeouts,
    };
  }

  // =========== LOCK STATE RETURN ===========
  return {
    ...lock,
    extendLock,
    acknowledgeExpiration,
    clearLock,
    clearTimeouts,
  };
};

export default useLockLoad;
